import type { ConstructionTimelinePhase } from "../types";

export function sortConstructionPhases(phases: ConstructionTimelinePhase[]): ConstructionTimelinePhase[] {
  return [...phases].sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
}

export function constructionPlanTotalDays(phases: ConstructionTimelinePhase[]): number {
  return phases.reduce((s, p) => s + Math.max(0, Math.floor(Number(p.approxDays)) || 0), 0);
}

/** Integer “nice” step for day axis labels (1, 2, 5, 10, 20, …). */
function pickNiceStepDays(rough: number): number {
  const r = Math.max(1, rough);
  const exp = Math.floor(Math.log10(r));
  const f = r / 10 ** exp;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return Math.max(1, Math.round(nf * 10 ** exp));
}

export type GanttAxisScaleOpts = {
  /** Sum of logged actual days across phases; extends axis when longer than the plan. */
  loggedTotalDays?: number;
};

/**
 * Axis upper bound and tick positions from the plan (and optional logged total),
 * so short plans use the chart width instead of a fixed long scale.
 */
export function ganttAxisScale(planTotalDays: number, opts?: GanttAxisScaleOpts): { axisMax: number; ticks: number[] } {
  const plan = Math.max(0, Math.floor(Number(planTotalDays)) || 0);
  const loggedRaw = opts?.loggedTotalDays;
  const logged =
    typeof loggedRaw === "number" && Number.isFinite(loggedRaw) ? Math.max(0, Math.floor(loggedRaw)) : 0;
  const content = Math.max(1, plan, logged);
  const padded = content + Math.max(1, Math.ceil(content * 0.05));

  const divisions = 5;
  let step = pickNiceStepDays(padded / divisions);
  let axisMax = Math.max(step, Math.ceil(padded / step) * step);

  while (step > 1 && axisMax / step > 10) {
    step = pickNiceStepDays(step * 2);
    axisMax = Math.max(step, Math.ceil(padded / step) * step);
  }

  const ticks: number[] = [];
  for (let d = 0; d <= axisMax + 1e-6; d += step) {
    ticks.push(Math.round(d));
  }
  if (ticks.length === 0) {
    return { axisMax: 1, ticks: [0, 1] };
  }
  if (ticks[ticks.length - 1] !== axisMax) {
    ticks.push(axisMax);
  }
  return { axisMax, ticks };
}
